import { FC, useCallback, useEffect, useMemo, useState } from "react";
import PageContainer from "src/pages/components/PageContainer";
import { getUserCompanyId, isUserAdministrator, isUserCompany } from "src/utils/auth";
import CompanyRequestsTable from "./components/CompanyRequestsTable";
import { CompanyRequest, getCompanyRequestsForCompany } from "src/apis/company_requests";
import { Id } from "src/utils/types";
import RegisterFab from "src/pages/components/RegisterFab";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import CompanySelect from "src/pages/components/CompanySelect";
import FilterButtons from "src/pages/components/SearchAndResetButtons";
import { useCompanyBlocks } from "src/context/CompanyBlocksContext";

const CompanyRequests: FC = (): JSX.Element => {
  const [companyRequests, setCompanyRequests] = useState<CompanyRequest[]>([]);
  const [companyId, setCompanyId] = useState<Id>(-1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isAdmin = useMemo<boolean>(() => isUserAdministrator(), []);
  const { isActionRestricted } = useCompanyBlocks();
  const isRequestBlocked = isActionRestricted("create_request");

  const fetchCompanyRequests = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const id: Id = isAdmin ? companyId : getUserCompanyId();
      const res: CompanyRequest[] = await getCompanyRequestsForCompany(id, statusFilter);

      setCompanyRequests(res);
      setError('');
    } catch (err: any) {
      setError(err.error);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, isAdmin, statusFilter]);

  useEffect(() => {
    if (!isAdmin) {
      fetchCompanyRequests()
    };
  }, [fetchCompanyRequests, isAdmin])

  return (
    <PageContainer title="Solicitações" error={error} guard={isUserCompany}>
      {!isAdmin && (
        <RegisterFab
          registerUrl="/solicitacoes/cadastro"
          isDisabled={isRequestBlocked}
          disabledReason={isRequestBlocked ? "Esta ação está temporariamente suspensa para a empresa." : undefined}
          label={isRequestBlocked ? "Cadastro bloqueado" : "Cadastrar"}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
          justifyContent: "center",
          alignItems: "center",
          width: "100%"
        }}
        >
        {isAdmin && (
          <CompanySelect
            isFilter
            useAllOption={isAdmin}
            companyId={companyId}
            setCompanyId={setCompanyId}
            setError={setError}
          />
        )}
        <FormControl>
          <InputLabel id="company-select">Status</InputLabel>

          <Select
            label="Status"
            labelId="company-select"
            value={statusFilter}
            variant="outlined"
            onChange={(e) => {
              setStatusFilter(e.target.value)
            }}
            sx={{
              width: '200px'
            }}
          >
            <MenuItem value={'ALL'}>
              Todos
            </MenuItem>
            <MenuItem value={'RESOLVED_ONLY'}>
              Apenas Resolvidos
            </MenuItem>
            <MenuItem value={'NON_RESOLVED_ONLY'}>
              Apenas Não Resolvidos
            </MenuItem>
          </Select>
        </FormControl>
        <FilterButtons
          isLoading={isLoading}
          isDisabled={(isAdmin && !companyId)}
          search={() => {
            fetchCompanyRequests();
          }}
          resetState={() => {
            setCompanyId('');
            setError('');
            setCompanyRequests([]);
          }}
        />
      </Box>

      <CompanyRequestsTable
        companyRequests={companyRequests}
        setError={setError}
      />
    </PageContainer>
  )
}

export default CompanyRequests;
